import { useState } from 'react'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconCircleCheck, IconPlugConnectedX } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { RestrictedState } from '../access/RestrictedState'
import { useAcknowledgeEtlHalt, useEtlHaltCapacity } from '../../hooks/useEtlHalts'
import { DETAIL_ADMIN_ONLY, NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import { haltDetailView } from '../../utils/haltDetail'
import { haltReasonText } from './requestFormat'
import {
  CAPACITY_CAPTION,
  CLEAR_CONSEQUENCES,
  NOTE_MAX_LENGTH,
  capacityView,
  clearErrorText,
  clearToast,
  confirmEnabled,
  remainingHaltsText,
} from './haltClearFormat'
import type { CapacityView } from './haltClearFormat'
import type { EtlHalt, EtlStorageFile } from '../../types/etlHalts'

/**
 * Clears one ETL halt — the Scheduler's only write besides Run, and like Run pinned to an Entra sign-in.
 *
 * Opened only from the full halt list (never the catalogue's summary), and only when the server says
 * `canClearHalts`. It shows what is being cleared (run, reason, diagnosis), what still blocks afterwards (from the
 * list the button came from), what clearing does and does not do, and the storage headroom read fresh on open.
 * A note is required. When the reserve is still short, clearing needs a second, explicit "I know".
 *
 * An error keeps the dialog open with the note intact, so a retry is one click; success closes it and says what
 * the server answered — including "someone else already cleared it; your note was not recorded".
 */
export function ClearHaltModal({
  halt,
  active,
  entraConfigured = true,
  onClose,
}: {
  /** The halt to clear; null keeps the dialog closed. */
  halt: EtlHalt | null
  /** The blocking halts the button came from, for "what still blocks after this". */
  active: readonly EtlHalt[]
  /** Whether this build can sign anyone in with Entra; words a sign-in refusal (never "Sign in again" without it). */
  entraConfigured?: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const opened = halt !== null
  const capacityQuery = useEtlHaltCapacity(opened)
  const clear = useAcknowledgeEtlHalt()
  const [note, setNote] = useState('')
  const [ackShort, setAckShort] = useState(false)

  const capacity = capacityView(capacityQuery)
  const canConfirm = confirmEnabled(note, capacity.kind, ackShort) && !clear.isPending
  const error = clear.isError ? clearErrorText(clear.error, entraConfigured) : null

  const close = () => {
    clear.reset()
    onClose()
  }

  if (!halt) {
    return <Modal opened={false} onClose={close} title="Clear halt" />
  }

  // Defensive: the clear is offered only to an administrator, who is never withheld the detail — but if the list
  // the halt came from was withheld, say so rather than "No detail was recorded".
  const detail = haltDetailView(halt.detail, halt.detailWithheld)

  const submit = () => {
    if (!canConfirm) return
    clear.mutate(
      { runId: halt.runId, note: note.trim() },
      {
        onSuccess: (response) => {
          const { title, message, color, autoClose } = clearToast(response)
          notifications.show({ title, message, color, ...(autoClose === false ? { autoClose: false } : {}) })
          close()
        },
      },
    )
  }

  return (
    <Modal opened={opened} onClose={close} title={`Clear the halt on ${halt.runKey}`} size="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Group gap="xs" wrap="wrap">
            <Badge size="sm" color="red" variant="light">
              {haltReasonText(halt.reason)}
            </Badge>
            <Text size="sm">on run</Text>
            <Anchor
              component="button"
              type="button"
              size="sm"
              ff="monospace"
              onClick={() => {
                close()
                navigate(`/execution-history/${encodeURIComponent(halt.runId)}`)
              }}
            >
              {halt.runKey}
            </Anchor>
            <Text size="xs" c="dimmed">
              halted {whenLabel(halt.haltedAt)}
            </Text>
          </Group>
          {detail.kind === 'withheld' ? (
            <Text size="sm" c="dimmed" data-state="detail-withheld">
              {DETAIL_ADMIN_ONLY}
            </Text>
          ) : (
            <Text size="sm">{(detail.kind === 'recorded' ? detail.diagnosis : null) ?? 'No detail was recorded.'}</Text>
          )}
        </Stack>

        <Text size="sm">{remainingHaltsText(active, halt.runId)}</Text>
        <Text size="sm" fw={500}>
          {CLEAR_CONSEQUENCES}
        </Text>

        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Storage headroom
          </Text>
          <CapacityReadout view={capacity} />
          <Text size="xs" c="dimmed">
            {CAPACITY_CAPTION}
          </Text>
        </Stack>

        <Textarea
          label="What was done about it"
          description={
            'Required. Recorded with the clear, so the next person to hit a halt can see how this one was '
            + 'resolved. The audit trail keeps it in full.'
          }
          required
          autosize
          minRows={2}
          maxLength={NOTE_MAX_LENGTH}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
        />

        {capacity.kind === 'short' && (
          <Checkbox
            label="I know the reserve is still short"
            checked={ackShort}
            onChange={(event) => setAckShort(event.currentTarget.checked)}
          />
        )}

        {error && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" title="The halt was not cleared">
            <Text size="sm">{error}</Text>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button color="red" loading={clear.isPending} disabled={!canConfirm} onClick={submit}>
            Clear this halt
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/** The capacity readout: six states, and only a served "sufficient" reads as intact. */
function CapacityReadout({ view }: { view: CapacityView }) {
  switch (view.kind) {
    case 'loading':
      return (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">
            {view.text}
          </Text>
        </Group>
      )
    case 'restricted':
      return <RestrictedState surface="The storage headroom readout" compact />
    case 'failed':
      return (
        <Alert icon={<IconPlugConnectedX size={16} />} color="yellow" variant="light" title={view.text}>
          {view.summary && <Text size="xs">{view.summary}</Text>}
        </Alert>
      )
    case 'intact':
      return (
        <Stack gap={4}>
          <Group gap={6}>
            <IconCircleCheck size={14} style={{ color: 'var(--status-good-fg)' }} />
            <Text size="sm">{view.text}</Text>
          </Group>
          <Text size="xs" c="dimmed">
            {view.detail}
          </Text>
          <FileTable files={view.files} />
        </Stack>
      )
    case 'short':
      return (
        <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light" title={view.text}>
          <Stack gap={4}>
            <Text size="sm">{view.detail}</Text>
            <Text size="sm">Clearing will not make room: the next load will likely halt again.</Text>
            <FileTable files={view.files} />
          </Stack>
        </Alert>
      )
    case 'unmeasured':
      return (
        <Group gap={6} wrap="nowrap" align="flex-start">
          <Text size="sm" ff="monospace">
            {view.text}
          </Text>
          <Text size="sm" c="dimmed">
            Not measured. {view.detail}
          </Text>
        </Group>
      )
    default:
      return <Text size="sm">{NOT_MEASURED}</Text>
  }
}

function FileTable({ files }: { files: EtlStorageFile[] }) {
  if (files.length === 0) return null
  return (
    <Table withRowBorders={false}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>File</Table.Th>
          <Table.Th>Kind</Table.Th>
          <Table.Th ta="right">Headroom (MB)</Table.Th>
          <Table.Th ta="right">Used / allocated (MB)</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {files.map((file) => (
          <Table.Tr key={`${file.fileKind}:${file.fileName}`}>
            <Table.Td>
              <Text size="xs" ff="monospace">
                {file.fileName}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs">{file.fileKind === 'LOG' ? 'Log' : 'Data'}</Text>
            </Table.Td>
            <Table.Td ta="right">
              <Text size="xs">{exactNumber(file.headroomMB)}</Text>
            </Table.Td>
            <Table.Td ta="right">
              <Text size="xs">
                {exactNumber(file.usedMB)} / {exactNumber(file.allocatedMB)}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}
